import React from 'react';
import { useTranslation, Trans } from 'react-i18next';

const STATUS_KEYS: Array<{ key: string; color: string }> = [
  { key: 'scheduled', color: 'bg-yellow-100 text-yellow-800' },
  { key: 'confirmed', color: 'bg-green-100 text-green-800' },
  { key: 'completed', color: 'bg-blue-100 text-blue-800' },
  { key: 'cancelled', color: 'bg-red-100 text-red-800' },
  { key: 'no_show', color: 'bg-gray-200 text-gray-700' },
];

// Reihenfolge der Kunden-E-Mails in der Übersichtstabelle (siehe help.email.rows.*).
const EMAIL_ROWS: string[] = ['confirmed', 'rescheduled', 'reminder', 'cancelled', 'no_show'];

const HelpPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('help.title')}</h1>
        <p className="text-sm text-gray-600">{t('help.subtitle')}</p>
      </div>

      {/* Status Workflow */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('help.status.heading')}</h2>
        <p className="text-sm text-gray-700 mb-4">
          <Trans i18nKey="help.status.intro" components={{ 1: <strong /> }} />
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-500">{t('help.status.colStatus')}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">{t('help.status.colMeaning')}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">{t('help.status.colWhenUse')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {STATUS_KEYS.map(s => (
                <tr key={s.key}>
                  <td className="px-3 py-3"><span className={`px-2 py-1 text-xs rounded-full ${s.color}`}>{t(`help.status.${s.key}.label`)}</span></td>
                  <td className="px-3 py-3 text-gray-700">{t(`help.status.${s.key}.meaning`)}</td>
                  <td className="px-3 py-3 text-gray-600">{t(`help.status.${s.key}.whenUse`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* What changes happen */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('help.changes.heading')}</h2>
        <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
          <li>{t('help.changes.item1')}</li>
          <li><Trans i18nKey="help.changes.item2" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.changes.item3" components={{ 1: <strong /> }} />
            <ul className="ml-6 mt-1 space-y-1 list-circle list-inside text-gray-600">
              <li><Trans i18nKey="help.changes.item3a" components={{ 1: <code />, 3: <em /> }} /></li>
              <li><Trans i18nKey="help.changes.item3b" components={{ 1: <em /> }} /></li>
            </ul>
          </li>
          <li><Trans i18nKey="help.changes.item4" components={{ 1: <strong /> }} /></li>
        </ul>
      </section>

      {/* Email behavior */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('help.email.heading')}</h2>
        <div className="space-y-4 text-sm text-gray-700">
          <div className="p-3 bg-green-50 border border-green-200 rounded">
            <strong className="text-green-900">{t('help.email.badge')}</strong>
          </div>
          <p>{t('help.email.intro')}</p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 align-top">{t('help.email.colTrigger')}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 align-top">{t('help.email.colMail')}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 align-top">{t('help.email.colContent')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {EMAIL_ROWS.map(r => (
                  <tr key={r}>
                    <td className="px-3 py-3 text-gray-700 align-top">{t(`help.email.rows.${r}.trigger`)}</td>
                    <td className="px-3 py-3 text-gray-900 font-medium align-top whitespace-nowrap">{t(`help.email.rows.${r}.mail`)}</td>
                    <td className="px-3 py-3 text-gray-600 align-top">{t(`help.email.rows.${r}.content`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="p-3 bg-teal-50 border border-teal-200 rounded text-teal-900"><Trans i18nKey="help.email.intake" components={{ 1: <strong /> }} /></p>
          <p><Trans i18nKey="help.email.bilingual" components={{ 1: <strong /> }} /></p>
          <p className="text-gray-600"><Trans i18nKey="help.email.toast" components={{ 1: <strong /> }} /></p>
        </div>
      </section>

      {/* How to change */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('help.where.heading')}</h2>
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
          <li><Trans i18nKey="help.where.item1" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.where.item2" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.where.item3" components={{ 1: <strong /> }} /></li>
        </ol>
      </section>

      {/* Google Calendar */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('help.gcal.heading')}</h2>
        <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
          <li>{t('help.gcal.item1')}</li>
          <li>{t('help.gcal.item2')}</li>
          <li><Trans i18nKey="help.gcal.item3" components={{ 1: <strong />, 3: <em />, 5: <code /> }} /></li>
          <li><Trans i18nKey="help.gcal.item4" components={{ 1: <strong /> }} /></li>
        </ul>
      </section>

      {/* Online-Buchung */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('help.online.heading')}</h2>
        <div className="space-y-3 text-sm text-gray-700">
          <p><Trans i18nKey="help.online.intro" components={{ 1: <strong /> }} /></p>
          <p className="font-semibold text-gray-800 mt-3">{t('help.online.flowTitle')}</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>{t('help.online.step1')}</li>
            <li>{t('help.online.step2')}</li>
            <li>{t('help.online.step3')}</li>
            <li>{t('help.online.step4')}</li>
            <li>{t('help.online.step5')}</li>
          </ol>
          <p className="mt-3"><Trans i18nKey="help.online.soft" components={{ 1: <strong /> }} /></p>
          <p><Trans i18nKey="help.online.pending" components={{ 1: <code />, 3: <code />, 5: <code />, 7: <code /> }} /></p>
        </div>
      </section>

      {/* Self-Service: absagen & verschieben */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('help.selfservice.heading')}</h2>
        <div className="space-y-3 text-sm text-gray-700">
          <p><Trans i18nKey="help.selfservice.intro" components={{ 1: <strong /> }} /></p>
          <p className="font-semibold text-gray-800 mt-3">{t('help.selfservice.rulesTitle')}</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><Trans i18nKey="help.selfservice.rule1" components={{ 1: <strong /> }} /></li>
            <li><Trans i18nKey="help.selfservice.rule2" components={{ 1: <strong /> }} /></li>
            <li>{t('help.selfservice.rule3')}</li>
          </ul>
          <p className="font-semibold text-gray-800 mt-3">{t('help.selfservice.whatTitle')}</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><Trans i18nKey="help.selfservice.what1" components={{ 1: <code className="px-1 bg-gray-100 rounded text-xs" /> }} /></li>
            <li>{t('help.selfservice.what2')}</li>
            <li>{t('help.selfservice.what3')}</li>
          </ul>
          <p className="font-semibold text-gray-800 mt-3">{t('help.selfservice.noticeTitle')}</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><Trans i18nKey="help.selfservice.notice1" components={{ 1: <strong />, 3: <code className="px-1 bg-gray-100 rounded text-xs" /> }} /></li>
            <li><Trans i18nKey="help.selfservice.notice2" components={{ 1: <strong /> }} /></li>
            <li><Trans i18nKey="help.selfservice.notice3" components={{ 1: <strong />, 3: <code className="px-1 bg-gray-100 rounded text-xs" /> }} /></li>
          </ul>
          <p className="mt-2"><Trans i18nKey="help.selfservice.noticeMail" components={{ 1: <strong /> }} /></p>
          <p className="mt-3"><Trans i18nKey="help.selfservice.config" components={{ 1: <code className="px-1 bg-gray-100 rounded text-xs" /> }} /></p>
        </div>
      </section>

      {/* Recalls: automatische Wiedereinbestellung */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('help.recall.heading')}</h2>
        <div className="space-y-3 text-sm text-gray-700">
          <p><Trans i18nKey="help.recall.intro" components={{ 1: <code className="px-1 bg-gray-100 rounded text-xs" /> }} /></p>
          <p className="font-semibold text-gray-800 mt-3">{t('help.recall.consentTitle')}</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><Trans i18nKey="help.recall.consent1" components={{ 1: <strong /> }} /></li>
            <li>{t('help.recall.consent2')}</li>
            <li>{t('help.recall.consent3')}</li>
          </ul>
          <p className="font-semibold text-gray-800 mt-3">{t('help.recall.trackTitle')}</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>{t('help.recall.track1')}</li>
            <li>{t('help.recall.track2')}</li>
            <li>{t('help.recall.track3')}</li>
          </ul>
          <p className="mt-3"><Trans i18nKey="help.recall.config" components={{ 1: <strong /> }} /></p>
        </div>
      </section>

      {/* Patient-Termin-Zuordnung */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('help.assignment.heading')}</h2>
        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1.5">
          <li>
            <Trans
              i18nKey="help.assignment.online"
              components={{ 1: <strong />, 3: <code className="px-1 bg-gray-100 rounded text-xs" /> }}
            />
          </li>
          <li><Trans i18nKey="help.assignment.manual" components={{ 1: <strong /> }} /></li>
        </ul>
      </section>

      {/* Resource-Type-Matching */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('help.roles.heading')}</h2>
        <div className="space-y-3 text-sm text-gray-700">
          <p><Trans i18nKey="help.roles.intro" components={{ 1: <strong /> }} /></p>

          <div className="overflow-x-auto mt-3">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('help.roles.colWhere')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('help.roles.colField')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('help.roles.colMeaning')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-3 py-3"><strong>{t('help.roles.rowProvidersWhere')}</strong></td>
                  <td className="px-3 py-3"><code>{t('help.roles.rowProvidersField')}</code></td>
                  <td className="px-3 py-3"><Trans i18nKey="help.roles.rowProvidersMeaning" components={{ 1: <em />, 3: <em /> }} /></td>
                </tr>
                <tr>
                  <td className="px-3 py-3"><strong>{t('help.roles.rowProceduresWhere')}</strong></td>
                  <td className="px-3 py-3"><code>{t('help.roles.rowProceduresField')}</code></td>
                  <td className="px-3 py-3">{t('help.roles.rowProceduresMeaning')}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-3 font-semibold text-gray-800">{t('help.roles.exampleTitle')}</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><Trans i18nKey="help.roles.example1" components={{ 1: <em /> }} /></li>
            <li><Trans i18nKey="help.roles.example2" components={{ 1: <em /> }} /></li>
            <li>{t('help.roles.example3')}</li>
            <li><Trans i18nKey="help.roles.example4" components={{ 1: <strong /> }} /></li>
          </ul>

          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded mt-3">
            <strong className="text-yellow-900">{t('help.roles.todoTitle')}</strong>
            <ul className="mt-1 list-disc list-inside space-y-1 text-yellow-900">
              <li><Trans i18nKey="help.roles.todo1" components={{ 1: <em />, 3: <strong /> }} /></li>
              <li><Trans i18nKey="help.roles.todo2" components={{ 1: <em />, 3: <strong /> }} /></li>
              <li><Trans i18nKey="help.roles.todo3" components={{ 1: <strong /> }} /></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Stammdaten */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('help.masters.heading')}</h2>
        <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
          <li><Trans i18nKey="help.masters.providers" components={{ 1: <strong />, 3: <strong />, 5: <strong /> }} /></li>
          <li><Trans i18nKey="help.masters.procedures" components={{ 1: <strong />, 3: <strong /> }} /></li>
          <li><Trans i18nKey="help.masters.operatories" components={{ 1: <strong /> }} /></li>
          <li>{t('help.masters.deactivate')}</li>
        </ul>
      </section>

      {/* Patientenerhebungsbogen */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('help.intake.heading')}</h2>

        <p className="text-sm text-gray-700 mb-4">
          <Trans i18nKey="help.intake.intro" components={{ 1: <strong /> }} />
        </p>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">{t('help.intake.s1Title')}</h3>
        <ul className="space-y-1.5 text-sm text-gray-700 list-disc list-inside mb-4">
          <li>
            <Trans
              i18nKey="help.intake.s1Item1"
              components={{ 1: <span className="mx-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold" /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="help.intake.s1Item2"
              components={{ 1: <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mx-0.5" /> }}
            />
          </li>
        </ul>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">{t('help.intake.s2Title')}</h3>
        <ul className="space-y-1.5 text-sm text-gray-700 list-disc list-inside mb-4">
          <li><Trans i18nKey="help.intake.s2Item1" components={{ 1: <strong />, 3: <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded" /> }} /></li>
          <li><Trans i18nKey="help.intake.s2Item2" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.intake.s2Item3" components={{ 1: <em /> }} /></li>
          <li>{t('help.intake.s2Item4')}</li>
        </ul>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">{t('help.intake.s3Title')}</h3>
        <ul className="space-y-1.5 text-sm text-gray-700 list-disc list-inside mb-4">
          <li><Trans i18nKey="help.intake.s3Item1" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.intake.s3Item2" components={{ 1: <strong /> }} /></li>
          <li>{t('help.intake.s3Item3')}</li>
        </ul>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">{t('help.intake.s4Title')}</h3>
        <div className="text-sm text-gray-700 space-y-2 mb-4">
          <ol className="space-y-1.5 list-decimal list-inside">
            <li>{t('help.intake.s4Item1')}</li>
            <li><Trans i18nKey="help.intake.s4Item2" components={{ 1: <strong /> }} /></li>
            <li><Trans i18nKey="help.intake.s4Item3" components={{ 1: <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded" /> }} /></li>
            <li>
              <Trans
                i18nKey="help.intake.s4Item4"
                components={{ 1: <strong />, 2: <a className="text-blue-600 hover:underline" href="mailto:info@seadent.es">info@seadent.es</a> }}
              />
            </li>
            <li><Trans i18nKey="help.intake.s4Item5" components={{ 1: <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded" /> }} /></li>
            <li><Trans i18nKey="help.intake.s4Item6" components={{ 1: <strong /> }} /></li>
          </ol>
        </div>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">{t('help.intake.s5Title')}</h3>
        <ul className="space-y-1.5 text-sm text-gray-700 list-disc list-inside mb-4">
          <li><Trans i18nKey="help.intake.s5Item1" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.intake.s5Item2" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.intake.s5Item3" components={{ 1: <strong />, 3: <strong />, 5: <strong />, 7: <strong /> }} /></li>
        </ul>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">{t('help.intake.s6Title')}</h3>
        <div className="text-sm text-gray-700 space-y-2 mb-2">
          <p>{t('help.intake.s6Intro')}</p>
          <ul className="space-y-1 list-disc list-inside ml-2">
            <li><Trans i18nKey="help.intake.s6Item1" components={{ 1: <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold" /> }} /></li>
            <li><Trans i18nKey="help.intake.s6Item2" components={{ 1: <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-semibold" />, 3: <strong /> }} /></li>
            <li><Trans i18nKey="help.intake.s6Item3" components={{ 1: <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-semibold" /> }} /></li>
          </ul>
          <p><Trans i18nKey="help.intake.s6Outro" components={{ 1: <strong /> }} /></p>
        </div>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">{t('help.intake.s7Title')}</h3>
        <ul className="space-y-1.5 text-sm text-gray-700 list-disc list-inside">
          <li><Trans i18nKey="help.intake.s7Item1" components={{ 1: <strong /> }} /></li>
          <li>{t('help.intake.s7Item2')}</li>
          <li>{t('help.intake.s7Item3')}</li>
        </ul>
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('help.docs.heading')}</h2>
        <p className="text-sm text-gray-700 mb-4">
          <Trans
            i18nKey="help.docs.intro"
            components={{
              1: <code className="px-1 bg-gray-100 rounded" />,
              3: <code className="px-1 bg-gray-100 rounded" />,
              5: <code className="px-1 bg-gray-100 rounded" />,
              7: <strong />,
            }}
          />
        </p>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">{t('help.docs.aTitle')}</h3>
        <p className="text-sm text-gray-700 mb-2">
          <Trans i18nKey="help.docs.aIntro" components={{ 1: <strong /> }} />
        </p>
        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1 mb-4">
          <li><Trans i18nKey="help.docs.a1" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.docs.a2" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.docs.a3" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.docs.a4" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.docs.a5" components={{ 1: <strong /> }} /></li>
          <li>
            <Trans
              i18nKey="help.docs.a6"
              components={{
                1: <strong />,
                3: <code className="px-1 bg-gray-100 rounded" />,
                5: <code className="px-1 bg-gray-100 rounded" />,
                7: <code className="px-1 bg-gray-100 rounded" />,
              }}
            />
          </li>
          <li><Trans i18nKey="help.docs.a7" components={{ 1: <strong />, 3: <strong /> }} /></li>
          <li><Trans i18nKey="help.docs.a8" components={{ 1: <strong />, 3: <strong /> }} /></li>
        </ol>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">{t('help.docs.bTitle')}</h3>
        <p className="text-sm text-gray-700 mb-2">
          <Trans i18nKey="help.docs.bIntro" components={{ 1: <strong /> }} />
        </p>
        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-2 mb-4">
          <li><Trans i18nKey="help.docs.b1" components={{ 1: <strong /> }} /></li>
          <li>{t('help.docs.b2')}</li>
          <li>{t('help.docs.b3')}
            <ul className="list-disc list-inside ml-5 mt-1 space-y-1 text-gray-700">
              <li><Trans i18nKey="help.docs.b3a" components={{ 1: <strong /> }} /></li>
              <li><Trans i18nKey="help.docs.b3b" components={{ 1: <strong /> }} /></li>
              <li><Trans i18nKey="help.docs.b3c" components={{ 1: <strong /> }} /></li>
            </ul>
          </li>
          <li><Trans i18nKey="help.docs.b4" components={{ 1: <strong /> }} /></li>
          <li>{t('help.docs.b5')}
            <ul className="list-disc list-inside ml-5 mt-1 space-y-1 text-gray-700">
              <li><Trans i18nKey="help.docs.b5a" components={{ 1: <strong /> }} /></li>
              <li><Trans i18nKey="help.docs.b5b" components={{ 1: <strong /> }} /></li>
              <li><Trans i18nKey="help.docs.b5c" components={{ 1: <strong /> }} /></li>
              <li><Trans i18nKey="help.docs.b5d" components={{ 1: <strong /> }} /></li>
            </ul>
          </li>
          <li><Trans i18nKey="help.docs.b6" components={{ 1: <strong />, 3: <strong /> }} /></li>
        </ol>
        <p className="text-sm text-gray-700 mb-4">
          <Trans i18nKey="help.docs.bOutro" components={{ 1: <strong /> }} />
        </p>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">{t('help.docs.cTitle')}</h3>
        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1 mb-4">
          <li><Trans i18nKey="help.docs.c1" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.docs.c2" components={{ 1: <strong />, 3: <code className="px-1 bg-gray-100 rounded" /> }} /></li>
          <li>{t('help.docs.c3')}</li>
          <li><Trans i18nKey="help.docs.c4" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.docs.c5" components={{ 1: <strong /> }} /></li>
        </ol>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">{t('help.docs.dTitle')}</h3>
        <p className="text-sm text-gray-700 mb-4">
          <Trans i18nKey="help.docs.dText" components={{ 1: <strong /> }} />
        </p>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">{t('help.docs.eTitle')}</h3>
        <p className="text-sm text-gray-700 mb-2">{t('help.docs.eIntro')}</p>
        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1 mb-4">
          <li><Trans i18nKey="help.docs.e1" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.docs.e2" components={{ 1: <strong />, 3: <code className="px-1 bg-gray-100 rounded" /> }} /></li>
          <li>{t('help.docs.e3')}</li>
        </ol>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">{t('help.docs.fTitle')}</h3>
        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 mb-4">
          <li><Trans i18nKey="help.docs.f1" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.docs.f2" components={{ 1: <strong /> }} /></li>
          <li><Trans i18nKey="help.docs.f3" components={{ 1: <strong /> }} /></li>
        </ul>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">{t('help.docs.gTitle')}</h3>
        <dl className="text-sm text-gray-700 space-y-3 mb-2">
          <div>
            <dt className="font-semibold text-gray-800">{t('help.docs.q1')}</dt>
            <dd className="ml-4"><Trans i18nKey="help.docs.a1q" components={{ 1: <strong /> }} /></dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-800">{t('help.docs.q2')}</dt>
            <dd className="ml-4">{t('help.docs.a2q')}</dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-800">{t('help.docs.q3')}</dt>
            <dd className="ml-4">{t('help.docs.a3q')}</dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-800">{t('help.docs.q4')}</dt>
            <dd className="ml-4"><Trans i18nKey="help.docs.a4q" components={{ 1: <em />, 3: <em /> }} /></dd>
          </div>
        </dl>
      </section>

      {/* Powered by LEANOFY */}
      <footer className="mt-12 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
        {t('help.footer.poweredBy')}{' '}
        <a
          href="https://www.leanofy.de"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-brand-primary hover:underline"
        >
          LEANOFY
        </a>
        {' · '}
        {t('help.footer.support')}{' '}
        <a
          href="https://www.leanofy.de"
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-primary hover:underline"
        >
          www.leanofy.de
        </a>
      </footer>
    </div>
  );
};

export default HelpPage;
